import React from 'react';
import PropTypes from 'prop-types';
import {isNil, isEmpty, isEqual, defaultsDeep, get} from 'lodash';
import ViPropTypes from '../propTypes';
import DesktopDashboard from './DesktopDashboard';
import MobileDashboard from './MobileDashboard';
import DashboardFrameData from './DashboardFrameData';
import {getTimelineData, getVideoTime} from '../util/media';

const processTimelineData = (media, customerSettings, userSettings, notAvailableSymbol) => {
  const systemMeasure = get(userSettings, 'settings.systemMeasure', 'imperial');

  return getTimelineData(media, systemMeasure).reduce((frames, frame) => {
    const dashboardFrame = new DashboardFrameData(systemMeasure, notAvailableSymbol);

    if (!isNil(frame.speedLimit)) {
      dashboardFrame.setSpeedLimit(
        frame.speedLimit,
        frame.j1708Speed,
        customerSettings
      );
    }

    if (!isNil(frame.j1708Speed)) {
      dashboardFrame.setEngineSpeed(frame.j1708Speed);
    }

    if (!isNil(frame.rpm)) {
      dashboardFrame.setRpm(frame.rpm);
    }

    if (!isNil(frame.gpsSpeed)) {
      dashboardFrame.setGpsSpeed(frame.gpsSpeed);
    }

    if (!isNil(frame.accelerationPedal)) {
      dashboardFrame.setAcceleration(frame.accelerationPedal);
    }

    if (!isNil(frame.forwardDistance)) {
      dashboardFrame.setForwardDistance(
        frame.forwardDistance,
        frame.forwardDistancePercent
      );
    }

    if (!isNil(frame.forwardSpeed)) {
      dashboardFrame.setForwardSpeed(
        frame.forwardSpeed,
        frame.forwardSpeedPercent
      );
    }

    dashboardFrame.setBrakeOn(frame.brakeOn);
    dashboardFrame.setClutchOn(frame.clutchOn);
    dashboardFrame.setCruiseActive(frame.cruiseActive);
    dashboardFrame.turnLightControl = frame.turnLightControl ? frame.turnLightControl : 0;

    frames[frame.time] = dashboardFrame;
    return frames;
  }, {});
};

export default class PlayerDashboard extends React.Component {

  static propTypes = {
    mediaLoaded: PropTypes.bool,
    mobile: PropTypes.bool,
    showEventLabel: PropTypes.bool,
    withTabs: PropTypes.bool,
    notAvailableSymbol: PropTypes.string,
    currentSecond: PropTypes.number,
    media: ViPropTypes.Media,
    customerSettings: ViPropTypes.CustomerSettings,
    userSettings: ViPropTypes.UserSettings
  }

  static defaultProps = {
    mediaLoaded: true,
    mobile: false,
    showEventLabel: false,
    withTabs: true,
    notAvailableSymbol: '--',
    currentSecond: 0,
    customerSettings: {
      gpsSpeed: true
    },
    userSettings: {
      settings: {
        systemMeasure: 'imperial'
      }
    }
  }

  state = {
    media: null,
    customerSettings: null,
    video: {
      start: 0
    },
    timelineData: {}
  }

  constructor(props) {
    super(defaultsDeep(props, PlayerDashboard.defaultProps));
  }

  getCurrentTime() {
    return this.state.video.start + (this.props.currentSecond * 1000);
  }

  getCurrentFrame() {
    const currentTime = this.getCurrentTime();
    return this.state.timelineData[currentTime] || new DashboardFrameData();
  }

  render() {
    const {mobile, ...otherProps} = this.props;
    const currentFrame = this.getCurrentFrame();

    if (mobile) {
      return (
        <MobileDashboard
          {...otherProps}
          currentFrame={currentFrame}
        />
      );
    }

    return (
      <DesktopDashboard
        {...otherProps}
        currentFrame={currentFrame}
      />
    );
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {};
    let processTimeline = false;

    if (!isEqual(props.media, state.media)) {
      newState.media = props.media;
      processTimeline = true;
    }

    if (!isEqual(props.customerSettings, state.customerSettings)) {
      newState.customerSettings = props.customerSettings;
      processTimeline = true;
    }

    if (!isEqual(props.userSettings, state.userSettings)) {
      newState.userSettings = props.userSettings;
      processTimeline = true;
    }

    if (processTimeline) {
      newState.video = getVideoTime(props.media);
      newState.timelineData = processTimelineData(
        props.media,
        props.customerSettings,
        props.userSettings,
        props.notAvailableSymbol
      );
    }

    if (isEmpty(newState)) {
      return null;
    }

    return newState;
  }

}
